/**
 * Note-taker mandate rule mirrored from the BE.
 *
 * The BE only enforces `err_note_taker_mandatory` when ALL of:
 *   1. job has `note_taker.mandatory = true`
 *   2. interview location is in `NOTETAKER_SUPPORTED_LOCATIONS`
 *      (`zoom / google_meet / teams`)
 *   3. FE explicitly sent `note_taker_enabled: false`
 *
 * For non-virtual locations (phone, onsite, HackerRank, CodeInterview)
 * the BE silently forces `note_taker_enabled = false` and accepts the
 * save — note-taker physically can't operate without a virtual
 * platform, so the mandate is implicitly waived.
 *
 * Sources:
 *   - `app/controllers/api_ats/v1/ats_interviews_controller.rb#validate_note_taker_setting_params`
 *   - `app/controllers/api_ats/v1/ats_schedules_controller.rb#validate_note_taker_setting`
 *   - `app/models/ats_interview.rb#NOTETAKER_SUPPORTED_LOCATIONS`
 *
 * Use `requiresNoteTakerEnabled` to decide whether the FE must ensure
 * `enable_note_taker: true` reaches the BE for the current location.
 */
export const NOTE_TAKER_VIRTUAL_LOCATIONS = [
  'zoom',
  'google_meet',
  'teams',
] as const;

export type NoteTakerVirtualLocation =
  (typeof NOTE_TAKER_VIRTUAL_LOCATIONS)[number];

export function isNoteTakerVirtualLocation(location: unknown): boolean {
  const loc = String(location ?? '');
  return NOTE_TAKER_VIRTUAL_LOCATIONS.includes(loc as NoteTakerVirtualLocation);
}

/**
 * `true` iff the FE must ship `enable_note_taker: true` to keep the BE
 * from raising `err_note_taker_mandatory` for this location. False for
 * non-virtual locations even when the mandate is on — the BE silently
 * accepts those.
 */
export function requiresNoteTakerEnabled(
  isNoteTakerMandatory: boolean,
  location: unknown
): boolean {
  return isNoteTakerMandatory && isNoteTakerVirtualLocation(location);
}
